import React, { useState } from 'react';
import { X, Film, Video } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card } from './ui/Card';
import { SwipefileReferencePicker } from './SwipefileReferencePicker';
import { cn } from '../lib/utils';
interface SwipefileReference {
  id: string;
  title: string;
  thumbnail: string;
  topic: string;
}
interface AddFormatProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (profile: {
    formatName: string;
    brandName: string;
    description: string;
    duration: string;
    style: string;
    keyElements: string[];
    swipefileReferences: SwipefileReference[];
  }) => void;
}
// Mock swipefile data
const MOCK_SWIPEFILE: SwipefileReference[] = [{
  id: '1',
  title: 'How Compound Interest Actually Works',
  thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60',
  topic: 'Financial Education'
}, {
  id: '2',
  title: '5 Banking Mistakes Gen Z Makes',
  thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60',
  topic: 'Gen Z Banking'
}, {
  id: '3',
  title: 'Understanding Your Credit Score',
  thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  topic: 'Credit & Loans'
}, {
  id: '4',
  title: 'Savings Hacks Nobody Tells You',
  thumbnail: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=800&auto=format&fit=crop&q=60',
  topic: 'Savings Tips'
}, {
  id: '5',
  title: "What Banks Don't Want You to Know",
  thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60',
  topic: 'Banking Secrets'
}, {
  id: '6',
  title: 'First Time Home Buyer Guide',
  thumbnail: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=60',
  topic: 'Mortgages'
}];
const BRAND_PROFILES = ['einfachbank Professional', 'einfachbank Gen Z'];
const DURATION_OPTIONS = ['30-45 seconds', '45-60 seconds', '60-90 seconds', '90-120 seconds', '2-3 minutes'];
export function AddFormatProfileModal({
  isOpen,
  onClose,
  onSubmit
}: AddFormatProfileModalProps) {
  const [formatName, setFormatName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [style, setStyle] = useState('');
  const [keyElementsInput, setKeyElementsInput] = useState('');
  const [selectedReferences, setSelectedReferences] = useState<SwipefileReference[]>([]);
  if (!isOpen) return null;
  const handleSubmit = () => {
    const keyElements = keyElementsInput.split(',').map(el => el.trim()).filter(el => el);
    if (!formatName.trim() || !brandName || !description.trim() || !duration || !style.trim() || keyElements.length === 0) return;
    onSubmit({
      formatName,
      brandName,
      description,
      duration,
      style,
      keyElements,
      swipefileReferences: selectedReferences
    });
    // Reset form
    setFormatName('');
    setBrandName('');
    setDescription('');
    setDuration('');
    setStyle('');
    setKeyElementsInput('');
    setSelectedReferences([]);
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-54">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white" data-id="element-55">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" data-id="element-56">
          <div className="flex items-center gap-3" data-id="element-57">
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center" data-id="element-58">
              <Film className="h-5 w-5 text-purple-600" data-id="element-59" />
            </div>
            <div data-id="element-60">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-61">
                New Format Profile
              </h2>
              <p className="text-sm text-gray-500" data-id="element-62">
                Define a content format for your brand
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-id="element-63">
            <X className="h-5 w-5" data-id="element-64" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-65">
          {/* Brand Selection */}
          <div data-id="element-66">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-67">
              Brand Profile *
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-68">
              {BRAND_PROFILES.map(brand => <button key={brand} onClick={() => setBrandName(brand)} className={cn('px-4 py-2 rounded-lg text-sm font-medium transition-all border', brandName === brand ? 'bg-[#139191] text-white border-[#139191]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#139191] hover:text-[#139191]')} data-id="element-69">
                  {brand}
                </button>)}
            </div>
          </div>

          {/* Format Name */}
          <div data-id="element-70">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-71">
              Format Name *
            </label>
            <Input placeholder="e.g., Talking Head, Whiteboard Session, Sketch Explainer" value={formatName} onChange={e => setFormatName(e.target.value)} className="text-base" data-id="element-72" />
          </div>

          {/* Description */}
          <div data-id="element-73">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-74">
              Description *
            </label>
            <textarea placeholder="Describe the format, its purpose, and how it should be executed..." value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#139191] focus:border-transparent resize-none text-sm" data-id="element-75" />
          </div>

          {/* Duration */}
          <div data-id="element-76">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-77">
              Typical Duration *
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-78">
              {DURATION_OPTIONS.map(dur => <button key={dur} onClick={() => setDuration(dur)} className={cn('px-3 py-1.5 rounded-full text-sm font-medium transition-colors', duration === dur ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')} data-id="element-79">
                  {dur}
                </button>)}
            </div>
          </div>

          {/* Style */}
          <div data-id="element-80">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-81">
              Visual Style *
            </label>
            <Input placeholder="e.g., Professional & Authoritative, Casual & Authentic" value={style} onChange={e => setStyle(e.target.value)} className="text-base" data-id="element-82" />
          </div>

          {/* Key Elements */}
          <div data-id="element-83">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-84">
              Key Elements *
            </label>
            <Input placeholder="e.g., Eye contact, Clean background, Subtitles, Professional lighting (comma-separated)" value={keyElementsInput} onChange={e => setKeyElementsInput(e.target.value)} className="text-base" data-id="element-85" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-86">
              Separate multiple elements with commas
            </p>
          </div>

          {/* Swipefile References */}
          <div data-id="element-87">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-88">
              <Video className="h-4 w-4 text-purple-600" data-id="element-89" />
              Format Reference Videos (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-4" data-id="element-90">
              Select videos that exemplify this format's style and execution.
            </p>
            <SwipefileReferencePicker availableReferences={MOCK_SWIPEFILE} selectedReferences={selectedReferences} onSelectionChange={setSelectedReferences} data-id="element-91" />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50" data-id="element-92">
          <p className="text-sm text-gray-500" data-id="element-93">
            {selectedReferences.length > 0 && `${selectedReferences.length} reference${selectedReferences.length > 1 ? 's' : ''} selected`}
          </p>
          <div className="flex gap-3" data-id="element-94">
            <Button variant="outline" onClick={onClose} data-id="element-95">
              Cancel
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSubmit} disabled={!formatName.trim() || !brandName || !description.trim() || !duration || !style.trim() || !keyElementsInput.trim()} data-id="element-96">
              Create Format Profile
            </Button>
          </div>
        </div>
      </Card>
    </div>;
}